import { DEFAULT_AGENT_KIND, agentCapabilities, agentKindLabel, agentSupports, launchAccountAgentOf } from '@orgus/protocol';
import type { AgentKind, Checkout, Session, SessionStartMode, SessionStartParams } from '@orgus/protocol';
import { ChevronRight, GitBranchPlus, FolderGit2, MonitorPlay, SquareTerminal } from '@orgus/ui/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import type { KeyboardEvent } from 'react';
import {
  Button,
  Dialog,
  Field,
  ErrorState,
  Input,
  Pressable,
  Segmented,
  Select,
  Textarea,
  cn,
} from '@orgus/ui';
import type { SelectOption } from '@orgus/ui';
import { runAction, toUiError } from '../../lib/api.ts';
import type { UiError } from '../../lib/api.ts';
import { shortPath } from '@orgus/ui';
import { hasMod } from '@orgus/ui';
import { useOrgus, useMachines, useProjects } from '../../lib/store.ts';
import { toast } from '@orgus/ui';
import { modelOptionLabel, provenModels } from '../sessions/labels.ts';
import { DEFAULT_PERMISSIONS, fitPermissions, initialPermissions, modeHint, permissionOptions } from './permissions.ts';
import type { PermissionsByMode } from './permissions.ts';
import { agentIcon } from '../adapters/AgentKind.tsx';
import { unavailableReason } from '../adapters/Capabilities.tsx';
import { SEGMENTED_MAX_KINDS, agentMachineError, agentMachineNote, keepKind, startableKinds } from './agents.ts';
import { closeNewSession, readRemembered, remember, setPending, useNewSession } from './store.ts';
import { branchError, promptError, suggestBranch } from './validate.ts';
import { manualStart, useRoom } from '../scheduler/data.ts';
import { RoomNotice } from './RoomNotice.tsx';
import { accountOptionsOf, pickedAccountId, useLaunchAccountChoices } from '../launch-accounts/data.ts';
import { choiceButton, roomPlan } from './room.ts';
import type { RoomChoice } from './room.ts';

/** How a prompt names the agent: "What should Claude do?" */
function who(kind: AgentKind): string {
  return kind === 'claude-code' ? 'Claude' : agentKindLabel(kind);
}

type Where = 'checkout' | 'worktree';

function Label({ children }: { children: string }) {
  return <span className="text-sm font-medium text-fg-muted">{children}</span>;
}

function checkoutLabel(c: Checkout): string {
  const dirty = c.dirty ? `, ${c.dirty} changed` : '';
  return `${c.branch ?? 'Detached'} in ${shortPath(c.path, 44)}${c.kind === 'worktree' ? ' (worktree)' : ''}${dirty}`;
}

const NO_SESSIONS: Record<string, Session> = {};

/** Pick project, machine, checkout or new worktree, prompt, mode, permissions and model; posts session.start. */
export function NewSessionDialog() {
  const open = useNewSession((s) => s.open);
  const prefill = useNewSession((s) => s.prefill);
  const projects = useProjects();
  const machines = useMachines();
  const allCheckouts = useOrgus((s) => s.checkouts);
  // Only read while open: at fleet scale every update would otherwise re-render the closed dialog.
  const sessions = useOrgus((s) => (open ? s.sessions : NO_SESSIONS));

  const [projectId, setProjectId] = useState('');
  const [machineId, setMachineId] = useState('');
  const [where, setWhere] = useState<Where>('checkout');
  const [checkoutId, setCheckoutId] = useState('');
  const [branch, setBranch] = useState('');
  const [branchTouched, setBranchTouched] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [mode, setMode] = useState<SessionStartMode>('headless');
  // Each run mode keeps its own permission choice (headless cannot ask, so its default differs).
  const [perms, setPerms] = useState<PermissionsByMode>(DEFAULT_PERMISSIONS);
  const permissionMode = perms[mode];
  const setPermissionMode = (v: string) => setPerms((p) => ({ ...p, [mode]: v }));
  const [model, setModel] = useState('');
  const [agentKind, setAgentKind] = useState<AgentKind>(DEFAULT_AGENT_KIND);
  // The launch account (ADR 0071): '' leaves it to the project's default, else the machine.
  const [launchAccountId, setLaunchAccountId] = useState('');
  // ADR 0078: the chosen agent's own accounts (Claude Code, Codex or Gemini CLI), shown from two on.
  const accountChoices = useLaunchAccountChoices(launchAccountAgentOf(agentKind));
  const [showErrors, setShowErrors] = useState(false);
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState<UiError | null>(null);
  const [choice, setChoice] = useState<RoomChoice | null>(null);
  const promptRef = useRef<HTMLTextAreaElement>(null);

  const checkouts = useMemo(() => Object.values(allCheckouts), [allCheckouts]);
  const machineById = useMemo(() => new Map(machines.map((m) => [m.id, m])), [machines]);

  // Machines that hold a checkout of the chosen project.
  const projectMachines = useMemo(() => {
    const ids = new Set(checkouts.filter((c) => c.projectId === projectId).map((c) => c.machineId));
    return machines.filter((m) => ids.has(m.id));
  }, [checkouts, machines, projectId]);

  const machineCheckouts = useMemo(
    () =>
      checkouts
        .filter((c) => c.projectId === projectId && c.machineId === machineId)
        .sort((a, b) => Number(a.kind === 'worktree') - Number(b.kind === 'worktree') || a.path.localeCompare(b.path)),
    [checkouts, projectId, machineId],
  );
  const base = machineCheckouts.find((c) => c.kind === 'main') ?? machineCheckouts[0];
  const takenBranches = useMemo(() => machineCheckouts.map((c) => c.branch).filter((b): b is string => !!b), [machineCheckouts]);

  /** An online machine with a checkout of the project: a preferred one, else where it last ran, else the first. */
  const pickMachine = (pid: string, preferred: Array<string | undefined>): string => {
    const has = new Set(checkouts.filter((c) => c.projectId === pid).map((c) => c.machineId));
    const ok = (m: string | undefined): m is string => !!m && has.has(m) && !!machineById.get(m)?.online;
    const recent = Object.values(sessions)
      .filter((x) => x.projectId === pid && ok(x.machineId))
      .sort((a, b) => b.lastActivity - a.lastActivity)[0]?.machineId;
    return preferred.find(ok) ?? (ok(recent) ? recent : undefined) ?? machines.find((m) => ok(m.id))?.id ?? '';
  };

  // Defaults on open: the prefill, else what was used last, else the first usable option.
  useEffect(() => {
    if (!open) return;
    const r = readRemembered();
    const usable = (pid: string) => checkouts.some((c) => c.projectId === pid && machineById.get(c.machineId)?.online);
    const pid =
      [prefill.projectId, r.projectId].find((p): p is string => !!p && projects.some((x) => x.id === p)) ??
      // Else the project with the most recent session that can run somewhere.
      Object.values(sessions)
        .filter((x) => x.projectId && usable(x.projectId))
        .sort((x, y) => y.lastActivity - x.lastActivity)[0]?.projectId ??
      projects.find((p) => usable(p.id))?.id ??
      projects[0]?.id ??
      '';
    setProjectId(pid);
    const mid = pickMachine(pid, [prefill.machineId, r.machineId]);
    setMachineId(mid);
    const pre = prefill.checkoutId ? checkouts.find((c) => c.id === prefill.checkoutId) : undefined;
    const main = checkouts.find((c) => c.projectId === pid && c.machineId === mid && c.kind === 'main');
    setCheckoutId(pre?.id ?? main?.id ?? checkouts.find((c) => c.projectId === pid && c.machineId === mid)?.id ?? '');
    // Where it ran last (a prefilled checkout always means that checkout).
    setWhere(!prefill.checkoutId && r.where === 'worktree' ? 'worktree' : 'checkout');
    setBranch('');
    setBranchTouched(false);
    setPrompt('');
    // The agent used last, while the chosen machine still offers it.
    const kind = keepKind(r.agentKind, machineById.get(mid));
    setAgentKind(kind);
    setMode(r.mode === 'terminal' && agentSupports(kind, 'launch.terminal') ? 'terminal' : 'headless');
    setPerms(fitPermissions(initialPermissions(r), kind));
    setModel(r.model ?? '');
    setLaunchAccountId('');
    setShowErrors(false);
    setSubmitError(null);
    setBusy(false);
    // Only on open: later store updates must not reset what the person typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const chooseProject = (pid: string) => {
    setProjectId(pid);
    chooseMachine(pickMachine(pid, [machineId]), pid);
  };
  const chooseMachine = (mid: string, pid = projectId) => {
    setMachineId(mid);
    const list = checkouts.filter((c) => c.projectId === pid && c.machineId === mid);
    setCheckoutId((list.find((c) => c.kind === 'main') ?? list[0])?.id ?? '');
    // An agent the new machine does not have goes back to Claude Code.
    const kind = keepKind(agentKind, machineById.get(mid));
    if (kind !== agentKind) chooseAgent(kind);
  };

  const chooseAgent = (kind: AgentKind) => {
    setAgentKind(kind);
    // A mode, permission or model the new agent does not have falls back to its default.
    if (!agentSupports(kind, 'launch.terminal')) setMode('headless');
    setPerms((p) => fitPermissions(p, kind));
    if (model && !provenModels(Object.values(sessions), kind).includes(model)) setModel('');
  };

  // Suggest a branch from the prompt until the person types their own.
  const effectiveBranch = branchTouched ? branch : branch || suggestBranch(prompt, agentKind === 'claude-code' ? 'claude' : agentKind);

  const machine = machineById.get(machineId);
  const checkout = checkouts.find((c) => c.id === checkoutId);

  // Room on the chosen machine (ADR 0063 step 5): the person decides what happens when it is full.
  const room = useRoom(projectId || null, open);
  const plan = useMemo(() => roomPlan(room, machineId), [room, machineId]);
  const choiceKey = `${projectId}|${machineId}|${plan.needsChoice}|${plan.alternative?.machineId ?? ''}`;
  useEffect(() => setChoice(plan.needsChoice ? plan.defaultChoice : null), [choiceKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const picked = plan.needsChoice ? choice : null;
  const queueing = picked === 'room-here' || picked === 'room-any';

  const errors = {
    project: projectId ? null : projects.length ? 'Choose a project.' : 'No projects are being watched yet. Add one in Projects.',
    machine: !machineId
      ? projectMachines.length
        ? 'Choose a machine that is online.'
        : 'No machine has a checkout of this project.'
      : !machine?.online
        ? `${machine?.name ?? 'That machine'} is offline. Choose another.`
        : null,
    checkout: where === 'checkout' && !checkout ? 'Choose a checkout.' : where === 'worktree' && !base ? 'No checkout to branch from on this machine.' : null,
    branch: where === 'worktree' ? branchError(effectiveBranch, takenBranches) : null,
    agent: machine?.online ? agentMachineError(agentKind, machine) : null,
    prompt: queueing && !prompt.trim() ? 'A session that waits for room needs a prompt.' : promptError(prompt, mode, who(agentKind)),
  };
  const valid = Object.values(errors).every((e) => e == null);
  const show = (k: keyof typeof errors, touched = showErrors) => (touched ? (errors[k] ?? undefined) : undefined);

  // The agent's own models: its defaults (Claude Code's aliases), then models its sessions
  // actually answered with. A model that only ever failed ("not found") is not offered.
  const models = useMemo(() => provenModels(Object.values(sessions), agentKind), [sessions, agentKind]);
  const modelOptions = [{ value: '', label: 'Machine default' }, ...models.map((value) => ({ value, label: modelOptionLabel(value) }))];

  // Options fold away by default and remember being opened; they open by themselves when a field
  // in them needs fixing, so an error is never hidden.
  const [advanced, setAdvancedState] = useState(() => {
    try {
      return localStorage.getItem('orgus.newSession.options') === 'open';
    } catch {
      return false;
    }
  });
  const setAdvanced = (v: boolean) => {
    setAdvancedState(v);
    try {
      localStorage.setItem('orgus.newSession.options', v ? 'open' : 'closed');
    } catch {
      // Storage blocked: the choice lasts while the dialog is open.
    }
  };
  const hiddenError = showErrors && !!(errors.machine || errors.checkout || errors.branch);
  const advancedOpen = advanced || hiddenError;
  // Offered for Claude Code in the background, once there are two accounts to choose between.
  const offerAccount = !!accountChoices && mode === 'headless';
  const accountOptions = accountChoices ? accountOptionsOf(accountChoices, projectId) : [];
  const chosenAccount = offerAccount ? pickedAccountId(accountChoices, launchAccountId) : null;
  const summary = [
    machine?.name,
    where === 'worktree' ? `new worktree${effectiveBranch ? ` ${effectiveBranch}` : ''}` : checkout ? (checkout.branch ?? shortPath(checkout.path, 28)) : null,
    mode === 'terminal' ? 'Terminal' : 'Headless',
    permissionOptions(mode, agentKind).find((o) => o.value === permissionMode)?.label,
    modelOptions.find((o) => o.value === model)?.label,
    chosenAccount ? accountOptions.find((o) => o.value === chosenAccount)?.label : undefined,
  ]
    .filter(Boolean)
    .join(' · ');
  const terminalGap = unavailableReason(agentKind, 'launch.terminal');
  const permissionCap = agentCapabilities(agentKind).permissions;
  const permissionNote = permissionCap.support === 'partial' ? permissionCap.note : undefined;
  // Claude Code and Codex, then every ACP agent this machine has installed.
  const kinds = useMemo(() => startableKinds(machine), [machine]);
  const agentOptions = kinds.map((k) => {
    const Icon = agentIcon(k);
    return { value: k, label: agentKindLabel(k), icon: <Icon /> };
  });
  const agentNote = machine?.online ? agentMachineNote(agentKind, machine) : null;

  const projectOptions: SelectOption[] = projects.map((p) => ({ value: p.id, label: p.name }));
  const machineOptions: SelectOption[] = projectMachines.map((m) => ({
    value: m.id,
    label: m.online ? m.name : `${m.name} (offline)`,
    disabled: !m.online,
  }));
  const checkoutOptions: SelectOption[] = machineCheckouts.map((c) => ({ value: c.id, label: checkoutLabel(c) }));

  const submit = async () => {
    setShowErrors(true);
    if (!valid || busy || !machine) {
      if (errors.prompt) promptRef.current?.focus();
      return;
    }
    setBusy(true);
    setSubmitError(null);
    if (queueing) {
      await queue();
      return;
    }
    // "Start on <machine with room>": the same start there, in its main checkout (or the one a new worktree branches from).
    const alt = picked === 'move' && plan.alternative ? machineById.get(plan.alternative.machineId) : undefined;
    const altCheckout = alt ? (checkouts.filter((c) => c.projectId === projectId && c.machineId === alt.id).sort((a, b) => Number(a.kind === 'worktree') - Number(b.kind === 'worktree') || a.path.localeCompare(b.path))[0] ?? null) : null;
    const startOn = alt && altCheckout ? alt : machine;
    const target = alt && altCheckout ? altCheckout : where === 'worktree' ? base! : checkout!;
    const params: SessionStartParams = {
      checkoutId: target.id,
      prompt: prompt.trim(),
      mode,
      // Older agents know only Claude Code: the kind is sent only when it is another one.
      ...(agentKind !== DEFAULT_AGENT_KIND ? { agentKind } : {}),
      ...(permissionMode !== 'default' ? { permissionMode } : {}),
      ...(model ? { model } : {}),
      ...(where === 'worktree' ? { newWorktree: { branch: effectiveBranch.trim() } } : {}),
      ...(chosenAccount ? { launchAccountId: chosenAccount } : {}),
    };
    const since = Date.now();
    try {
      const res = await runAction(startOn.id, 'session.start', params);
      remember({ projectId, machineId: startOn.id, mode, permissionMode, permissionModes: perms, model, agentKind, where });
      const data = (res.data ?? {}) as { sessionId?: unknown; agentKind?: unknown };
      setPending({
        machineId: startOn.id,
        projectId,
        sessionId: typeof data.sessionId === 'string' ? data.sessionId : undefined,
        checkoutId: where === 'checkout' ? target.id : undefined,
        since,
      });
      const place = where === 'worktree' ? `a new worktree on ${effectiveBranch.trim()}` : (target.branch ?? shortPath(target.path, 32));
      // An agent that ignored agentKind says so by what it reports back (or by saying nothing).
      const started = typeof data.agentKind === 'string' ? data.agentKind : DEFAULT_AGENT_KIND;
      if (started !== agentKind) {
        toast(`${agentKindLabel(started)} started instead of ${agentKindLabel(agentKind)}`, {
          description: `Update the Orgus agent on ${startOn.name}, and stop the session in ${place} if you do not want it.`,
          tone: 'error',
        });
        closeNewSession();
        return;
      }
      toast.success(
        mode === 'terminal' ? 'Terminal opened' : 'Session started',
        mode === 'terminal'
          ? `${agentKindLabel(agentKind)} is open in a new terminal on ${startOn.name}, in ${place}.`
          : `${who(agentKind)} is working in ${place} on ${startOn.name}. It opens here when it appears.`,
      );
      closeNewSession();
    } catch (e) {
      setSubmitError(toUiError(e));
    } finally {
      setBusy(false);
    }
  };

  /** "Start when there is room": a mission the scheduler starts on this machine, or any, once one has room. */
  const queue = async () => {
    if (!machine) return;
    const here = picked === 'room-here';
    const pin = where === 'worktree' ? base : checkout;
    try {
      const r = await manualStart({
        projectId,
        prompt: prompt.trim(),
        when: 'room',
        machineId: here ? machine.id : null,
        ...(here && pin ? { checkoutId: pin.id } : {}),
        mode,
        model: model || null,
        permissionMode: permissionMode !== 'default' ? permissionMode : null,
        newWorktree: where === 'worktree' ? { branch: effectiveBranch.trim() } : null,
        ...(agentKind !== DEFAULT_AGENT_KIND ? { agentKind } : {}),
      });
      remember({ projectId, machineId, mode, permissionMode, permissionModes: perms, model, agentKind, where });
      const started = r.entry?.state === 'started' || r.entry?.state === 'starting';
      toast.success(
        started ? 'Session starting' : 'Session queued',
        started
          ? `A machine had room, so it is starting now. Follow it in Capacity.`
          : here
            ? `It starts on ${machine.name} when it has room. Follow it in Capacity.`
            : 'It starts on the first machine with the project that has room. Follow it in Capacity.',
      );
      closeNewSession();
    } catch (e) {
      setSubmitError(toUiError(e));
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && hasMod(e)) {
      e.preventDefault();
      void submit();
    }
  };

  const altMain = plan.alternative ? checkouts.filter((c) => c.projectId === projectId && c.machineId === plan.alternative!.machineId).sort((a, b) => Number(a.kind === 'worktree') - Number(b.kind === 'worktree'))[0] : undefined;
  const destination =
    picked === 'move' && plan.alternative && altMain
      ? `Runs on ${plan.alternative.name}, ${shortPath(altMain.path, 40)}`
      : picked === 'room-here' && machine
        ? `Waits for room on ${machine.name}`
        : picked === 'room-any'
          ? 'Waits for the first machine with room'
          : machine && (where === 'worktree' ? base : checkout)
            ? `Runs on ${machine.name}, ${where === 'worktree' ? `new worktree beside ${shortPath(base!.path, 30)}` : shortPath(checkout!.path, 40)}`
            : null;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !o && closeNewSession()}
      title="New session"
      description="Start a coding agent in any project, on any machine."
      size="lg"
      dismissible={!busy}
      initialFocus={() => promptRef.current}
      footer={
        <>
          {destination && (
            <span className="mr-auto hidden min-w-0 truncate text-sm text-fg-subtle sm:block" title={destination}>
              {destination}
            </span>
          )}
          <Button variant="ghost" onClick={closeNewSession} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy} disabled={!projects.length || (showErrors && !valid)} onClick={submit} shortcut={['mod', 'enter']}>
            {choiceButton(picked, plan, mode === 'terminal' ? 'Open in terminal' : 'Start session')}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-6 pb-1"
        onKeyDown={onKeyDown}
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        noValidate
      >
        {!projects.length && (
          // Nothing to run in: say what is missing and where to add it, instead of a form that cannot start.
          // With no machine at all, the step before a project is connecting one.
          <p role="status" className="rounded-md border border-border-subtle bg-raised px-3 py-2 text-sm text-fg-muted">
            {machines.length ? (
              <>
                There is no project to run in yet. A project is a git repository on a machine that runs the agent.{' '}
                <Link to="/projects" onClick={closeNewSession} className="text-accent-text underline-offset-2 hover:underline">
                  Add a project
                </Link>
                , then start a session.
              </>
            ) : (
              <>
                No machine is connected yet. A session runs Claude Code on one of your machines, in one of its repositories.{' '}
                <Link to="/getting-started?step=machine" onClick={closeNewSession} className="text-accent-text underline-offset-2 hover:underline">
                  Connect a machine
                </Link>
                , then add a project.
              </>
            )}
          </p>
        )}
        <Textarea
          ref={promptRef}
          label="Prompt"
          rows={4}
          value={prompt}
          placeholder={`What should ${who(agentKind)} do? For example: fix the flaky webhook retry test and run the suite.`}
          onChange={(e) => setPrompt(e.target.value)}
          error={show('prompt')}
          hint={mode === 'terminal' ? 'Optional in a terminal: leave it empty to start a conversation there.' : undefined}
        />

        <div className="grid gap-3 sm:grid-cols-2">
          <Select
            label="Project"
            options={projectOptions}
            value={projectId}
            placeholder={projects.length ? 'Choose a project' : 'No projects yet'}
            onValueChange={chooseProject}
            error={show('project')}
          />
          <Field label="Agent" className="min-w-0">
            {() => (
            <>
            {kinds.length <= SEGMENTED_MAX_KINDS ? (
              <Segmented<AgentKind> label="Agent" value={agentKind} onChange={chooseAgent} options={agentOptions} />
            ) : (
              // More agents than a segmented control holds: a select, each with its glyph.
              <Select aria-label="Agent" options={agentOptions} value={agentKind} onValueChange={chooseAgent} />
            )}
            {errors.agent ? (
              <p role="alert" className="text-sm text-failed">
                {errors.agent}
              </p>
            ) : agentNote ? (
              <p className="text-sm text-fg-subtle">{agentNote}</p>
            ) : (
              machine?.online &&
              machine.agentKinds &&
              !machine.runtimes && (
                <p className="text-sm text-fg-subtle">
                  {machine.name} is still checking which agents are installed. The start fails there if {agentKindLabel(agentKind)} is missing.
                </p>
              )
            )}
            </>
            )}
          </Field>
        </div>


        {/* Everything past the essentials, folded: one quiet line says what it is set to. */}
        <div className="flex flex-col gap-4">
          <Pressable
            onClick={() => setAdvanced(!advancedOpen)}
            aria-expanded={advancedOpen}
            className="flex min-w-0 items-center gap-2 self-stretch rounded-control text-left text-caption text-fg-subtle transition-colors duration-(--duration-fast) hover:text-fg-muted"
          >
            <ChevronRight className={cn('size-3 shrink-0 transition-transform duration-(--duration-fast)', advancedOpen && 'rotate-90')} aria-hidden />
            <span className="shrink-0 font-medium text-fg-muted">Options</span>
            {!advancedOpen && <span className="min-w-0 truncate">{summary}</span>}
          </Pressable>
          {advancedOpen && (
            <div className="flex flex-col gap-6">
        <div className="grid gap-3 sm:grid-cols-2">
          <Select
            label="Machine"
            options={machineOptions}
            value={machineId}
            placeholder={projectMachines.length ? 'Choose a machine' : 'No machine has this project'}
            onValueChange={(m) => chooseMachine(m)}
            error={show('machine', showErrors || (!!machineId && !machine?.online))}
          />
          <Field label="Where" className="min-w-0">
            {() => (
            <>
            <Segmented<Where>
              label="Where to run"
              value={where}
              onChange={setWhere}
              options={[
                { value: 'checkout', label: 'Existing checkout', icon: <FolderGit2 /> },
                { value: 'worktree', label: 'New worktree', icon: <GitBranchPlus /> },
              ]}
            />
            </>
            )}
          </Field>
        </div>

        <div className="flex flex-col gap-2">
          {where === 'checkout' ? (
            <Select
              aria-label="Checkout"
              options={checkoutOptions}
              value={checkoutId}
              placeholder={machineCheckouts.length ? 'Choose a checkout' : 'No checkouts on this machine'}
              onValueChange={setCheckoutId}
              error={show('checkout')}
              hint={checkout && checkout.dirty > 0 ? `${checkout.dirty} uncommitted changes here. ${who(agentKind)} works on top of them.` : undefined}
            />
          ) : (
            <Input
              aria-label="Branch name"
              value={effectiveBranch}
              placeholder="feat/short-name"
              onChange={(e) => {
                setBranchTouched(true);
                setBranch(e.target.value);
              }}
              onBlur={() => setBranchTouched(true)}
              error={branchTouched || showErrors ? (errors.branch ?? undefined) : undefined}
              hint={base ? `Branches from ${base.branch ?? 'the current commit'} in ${shortPath(base.path, 40)}.` : undefined}
              className="font-mono"
            />
          )}
        </div>


        <div className="grid gap-3 sm:grid-cols-[auto_1fr_1fr] sm:items-end">
          <Field label="Run" className="min-w-0">
            {() => (
            <>
            <Segmented<SessionStartMode>
              label="How to run"
              size="md"
              value={mode}
              onChange={setMode}
              options={[
                { value: 'headless', label: 'Headless', icon: <MonitorPlay /> },
                { value: 'terminal', label: 'Terminal', icon: <SquareTerminal />, disabled: !!terminalGap },
              ]}
            />
            </>
            )}
          </Field>
          <Select
            label="Permissions"
            options={permissionOptions(mode, agentKind)}
            value={permissionMode}
            onValueChange={setPermissionMode}
          />
          <Select label="Model" options={modelOptions} value={model} onValueChange={setModel} />
        </div>
        <p className="-mt-1 text-sm text-fg-subtle">
          {modeHint(mode, permissionMode, agentKind)}
        </p>
        {offerAccount && (
          <Select label="Account" options={accountOptions} value={chosenAccount ?? ''} onValueChange={setLaunchAccountId} />
        )}
        {(terminalGap || permissionNote) && (
          <p className="-mt-2 text-sm text-fg-subtle" data-agent-gaps>
            {terminalGap && `${agentKindLabel(agentKind)} cannot start in a terminal from here yet; run it headless. `}
            {permissionNote}
          </p>
        )}

            </div>
          )}
        </div>

        {machine?.online && <RoomNotice plan={plan} choice={picked} onChoice={setChoice} disabled={busy} />}

        {submitError && <ErrorState variant="inline" error={submitError} />}
      </form>
    </Dialog>
  );
}
